// ProfileFollowSystem — Follow/Unfollow butonu + followers/following sayaci
// API: POST/DELETE /api/users/{username}/follow
import { UserCheck, UserPlus, Users } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { API_URL, apiFetch } from '../../config/api'

interface Props {
  username: string
  isOwner: boolean
}

interface FollowData {
  followers_count: number
  following_count: number
  is_following: boolean
}

export default function ProfileFollowSystem({ username, isOwner }: Props) {
  const [data, setData] = useState<FollowData>({ followers_count: 0, following_count: 0, is_following: false })
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    apiFetch(`${API_URL}/api/users/${encodeURIComponent(username)}/stats`)
      .then(r => r.ok ? r.json() : null)
      .then((d: any) => {
        if (d) setData({
          followers_count: d.followers_count ?? d.followers ?? 0,
          following_count: d.following_count ?? d.following ?? 0,
          is_following: d.is_following ?? false,
        })
      })
      .catch(() => {})
  }, [username])

  const toggleFollow = useCallback(() => {
    if (busy || isOwner) return
    setBusy(true)
    const method = data.is_following ? 'DELETE' : 'POST'
    apiFetch(`${API_URL}/api/users/${encodeURIComponent(username)}/follow`, { method })
      .then(r => {
        if (r.ok) {
          setData(prev => ({
            ...prev,
            is_following: !prev.is_following,
            followers_count: prev.followers_count + (prev.is_following ? -1 : 1),
          }))
        }
        setBusy(false)
      })
      .catch(() => setBusy(false))
  }, [busy, data.is_following, isOwner, username])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      {/* Followers/Following sayaclari */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-secondary, #8b949e)' }}>
          <Users size={14} />
          <strong style={{ color: 'var(--text-primary, #e6edf3)' }}>{data.followers_count}</strong> Followers
        </span>
        <span style={{ fontSize: 13, color: 'var(--text-secondary, #8b949e)' }}>
          <strong style={{ color: 'var(--text-primary, #e6edf3)' }}>{data.following_count}</strong> Following
        </span>
      </div>

      {/* Follow butonu — sadece baskasinin profilinde */}
      {!isOwner && (
        <button
          onClick={toggleFollow}
          disabled={busy}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 18px', borderRadius: 10, border: 'none',
            fontSize: 13, fontWeight: 700, cursor: busy ? 'wait' : 'pointer',
            background: data.is_following
              ? 'rgba(255,255,255,0.06)'
              : 'linear-gradient(135deg, #667eea, #764ba2)',
            color: data.is_following ? 'var(--text-secondary, #8b949e)' : '#fff',
            boxShadow: data.is_following ? 'none' : '0 4px 16px rgba(102,126,234,0.3)',
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {data.is_following ? <UserCheck size={14} /> : <UserPlus size={14} />}
          {data.is_following ? 'Following' : 'Follow'}
        </button>
      )}
    </div>
  )
}
